import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	FormControlLabel,
	Grid,
	IconButton,
	Paper,
	Switch,
	TextField,
	Tooltip,
	Typography,
	useMediaQuery,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BlockIcon from "@mui/icons-material/Block";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import Swal from "sweetalert2";
import dayjs from "dayjs";
import { debounce } from "@mui/material/utils";
import { useTranslation } from "react-i18next";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Datagrid from "../Datagrid/Datagrid";
import DatagridLoading from "../DatagridLoading/DatagridLoading";
import {
	createPromotionCode,
	deactivatePromotionCode,
	getPromotionCodes,
	updatePromotionCode,
} from "../../services/promotionService";
import { getMachinesByProvision } from "../../services/analyticsService";
import { getProductsByMachineId, normalizeMachineProductsResponse } from "../../services/machineCardService";
import { getCustomersCusSites } from "../../services/manageCustomerMasterService";
import { getAllCustomerSites } from "../../services/manageSitesMasterService";
import { getUserAllocations } from "../../services/manageSitesService";
import { useAuth } from "../../context/authContext";
import { formatDateTime } from "../../utilities/globalServiceUtils";
import ModalWrapper from "../ModalWrapper/ModalWrapper";
import UserName from "../UserName/UserName";

const EMPTY_FORM = {
	code: "",
	discount_type: "percent",
	discount_value: "",
	start_date: "",
	end_date: "",
	single_use_per_user: false,
	is_active: true,
	customer_ids: [],
	customer_site_ids: [],
};

const MAX_PROMOTION_CODE_LENGTH = 50;

const EMPTY_BROAD_DISPLAY_STATE = {
	customer_ids: true,
	customer_site_ids: true,
};

const EMPTY_CREATE_BROAD_DISPLAY_STATE = {
	...EMPTY_BROAD_DISPLAY_STATE,
	customer_ids: false,
	customer_site_ids: false,
};

const toInputDate = (value) => {
	if (!value) return "";
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return "";
	return date.toISOString().slice(0, 10);
};
const toDayjsDate = (value) => {
	const normalized = toInputDate(value);
	return normalized ? dayjs(normalized) : null;
};

const getTodayDate = () => {
	const today = new Date();
	const timezoneOffsetMs = today.getTimezoneOffset() * 60 * 1000;
	return new Date(today.getTime() - timezoneOffsetMs).toISOString().slice(0, 10);
};

const isValidDateInput = (value) => {
	const normalized = String(value || "").trim();

	if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return false;

	const [year, month, day] = normalized.split("-").map(Number);
	const date = new Date(Date.UTC(year, month - 1, day));

	return (
		date.getUTCFullYear() === year &&
		date.getUTCMonth() + 1 === month &&
		date.getUTCDate() === day
	);
};

const formatDateDisplay = (value, emptyLabel = "Any") => {
	const normalized = toInputDate(value);
	if (!normalized) return emptyLabel;
	return `${normalized.slice(5, 7)}-${normalized.slice(8, 10)}-${normalized.slice(0, 4)}`;
};

const isValidPercentageInput = (value) => /^\d+(\.\d+)?$/.test(String(value || "").trim());

const ADMIN_GRID_SX = {
	"& .MuiDataGrid-cell": {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-start",
		textAlign: "left",
	},
	"& .MuiDataGrid-columnHeaderTitleContainer": {
		justifyContent: "flex-start",
	},
	"& .MuiDataGrid-columnHeaderTitle": {
		textAlign: "left",
	},
};

const toIdArray = (value) =>
	Array.from(
		new Set(
			(Array.isArray(value) ? value : [])
				.map((item) => Number(item))
				.filter((item) => Number.isFinite(item) && item > 0)
		)
	);

const resolveScopedSiteIds = ({
	sites = [],
	customerIds = [],
	explicitSiteIds = [],
} = {}) => {
	const normalizedExplicitSiteIds = toIdArray(explicitSiteIds);
	if (normalizedExplicitSiteIds.length) return normalizedExplicitSiteIds;

	const normalizedCustomerIds = new Set(toIdArray(customerIds));

	return toIdArray(
		sites
			.filter((site) =>
				normalizedCustomerIds.size === 0 ||
				normalizedCustomerIds.has(Number(site.cus_id))
			)
			.map((site) => Number(site.site_id))
	);
};

const idsFromOptions = (options = []) =>
	toIdArray(options.map((option) => option?.value));

const optionsByIds = (options = [], ids = []) => {
	const idSet = new Set(toIdArray(ids).map(String));
	return options.filter((option) => idSet.has(String(option?.value)));
};

const selectedOrAllOptions = (options = [], ids = []) => {
	const normalizedIds = toIdArray(ids);
	return normalizedIds.length ? optionsByIds(options, normalizedIds) : [];
};

const selectedOrAllOptionsForDisplay = (options = [], ids = [], emptyMeansAll = false) => {
	const normalizedIds = toIdArray(ids);
	if (normalizedIds.length) return optionsByIds(options, normalizedIds);
	return emptyMeansAll ? options : [];
};

const getCompactSelectionTooltip = (selected = []) =>
	selected
		.map((option) => option?.label || option?.value)
		.filter(Boolean)
		.join(", ");

/**
 * Renders Autocomplete selections as a compact chip row ("A", "B", "+N more").
 *
 * @param {Array<{value: any, label: string, isAllOption?: boolean}>} value - Selected options.
 * @param {(params: {index: number}) => object} getTagProps - MUI tag props factory (key, onDelete, ...).
 * @param {string} allLabel - Label shown when everything is selected (collapses to a single chip).
 * @param {number} totalOptions - Option count, used to detect the "all selected" case.
 * @param {number} maxVisible - How many chips to show before collapsing the rest into "+N more".
 * @returns {JSX.Element|Array} Chip row, or [] when nothing is selected.
 *
 * Chips use `flex: 0 1 auto` on purpose: `1 1 0` made every chip stretch to an equal
 * share of the field once 2+ were selected, so short labels rendered as oversized pills.
 * Width is capped by maxWidth + ellipsis instead.
 */
const renderCompactTags = (
	value = [],
	getTagProps,
	allLabel,
	totalOptions = 0,
	maxVisible = 2
) => {
	const selected = Array.isArray(value) ? value : [];

	if (!selected.length) {
		return [];
	}

	if (selected.some((option) => option?.isAllOption)) {
		return (
			<Chip
				size="small"
				label={allLabel || "All"}
				{...getTagProps({ index: 0 })}
			/>
		);
	}

	if (allLabel && totalOptions > 1 && selected.length >= totalOptions) {
		return (
			<Chip
				size="small"
				label={allLabel}
				{...getTagProps({ index: 0 })}
			/>
		);
	}

	const visibleSelected = selected.slice(0, maxVisible);
	const hiddenCount = selected.length - visibleSelected.length;

	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 0.5,
				overflow: "hidden",
				minWidth: 0,
				maxWidth: "100%",
			}}
		>
			{visibleSelected.map((option, index) => (
				<Chip
					key={option?.value || index}
					size="small"
					label={option?.label || option?.value}
					{...getTagProps({ index })}
					sx={{
						flex: "0 1 auto",
						minWidth: 0,
						maxWidth: { xs: 120, md: 140, xl: 180 },
						"& .MuiChip-label": {
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						},
					}}
				/>
			))}

			{hiddenCount > 0 && (
				<Tooltip title={getCompactSelectionTooltip(selected)} arrow>
					<Chip
						size="small"
						label={`+${hiddenCount} more`}
						sx={{ cursor: "default", flexShrink: 0 }}
					/>
				</Tooltip>
			)}
		</Box>
	);
};

const arrayIntersects = (left = [], right = []) => {
	const rightSet = new Set(toIdArray(right).map(String));
	return toIdArray(left).some((id) => rightSet.has(String(id)));
};

const getPromotionCode = (row = {}) => row.code || row.coupon_code || "";

const formatDiscount = (row = {}) => {
	const type = row.discount_type || row.unit;
	const value = row.discount_value ?? row.amount;
	return type === "percent" || type === "percentage" ? `${value}%` : value;
};

const getScopedOptionLabels = (options = [], ids = []) => {
	const optionMap = new Map(options.map((option) => [String(option?.value), option?.label]));
	return toIdArray(ids)
		.map((id) => optionMap.get(String(id)))
		.filter(Boolean);
};

const isAllScopeSelection = (options = [], ids = []) => {
	const normalizedIds = toIdArray(ids);
	return options.length > 1 && normalizedIds.length >= options.length;
};

const formatScopePart = (label, names = [], fallbackCount = 0, fallbackSingular = label, fallbackPlural = `${fallbackSingular}s`) => {
	if (names.length) return `${label}: ${names.join(", ")}`;
	if (!fallbackCount) return null;
	return `${fallbackCount} ${fallbackCount === 1 ? fallbackSingular : fallbackPlural}`;
};

const formatScopeList = (row = {}, scopeOptions = {}) => {
	const customerCount = toIdArray(row.customer_ids).length;
	const siteCount = toIdArray(row.customer_site_ids).length;
	const bakeriesAll =
		customerCount === 0 || isAllScopeSelection(scopeOptions.customersOptions, row.customer_ids);
	const locationsAll =
		siteCount === 0 || isAllScopeSelection(scopeOptions.sitesOptions, row.customer_site_ids);
	if (bakeriesAll && locationsAll) {
		return ["All Bakeries", "All Bakery Locations"];
	}
	const bakeryNames = getScopedOptionLabels(scopeOptions.customersOptions, row.customer_ids);
	const bakeryLocationNames = getScopedOptionLabels(scopeOptions.sitesOptions, row.customer_site_ids);
	return [
		bakeriesAll ? "All Bakeries" : formatScopePart("Bakery", bakeryNames, customerCount, "Bakery", "Bakeries"),
		locationsAll ? "All Bakery Locations" : formatScopePart("Location", bakeryLocationNames, siteCount, "Location", "Locations"),
	].filter(Boolean);
};

const formatScope = (row = {}, scopeOptions = {}) => {
	const parts = formatScopeList(row, scopeOptions);
	return parts.length ? parts.join(", ") : "Scoped orders";
};

const getErrorMessage = (response, fallback) => {
	if (Array.isArray(response?.error)) return response.error.join(", ");
	if (Array.isArray(response?.data)) return response.data.join(", ");
	return response?.message || response?.error_message || fallback;
};

const getSavedPromotionRow = (response) =>
	response?.data && !Array.isArray(response.data) ? response.data : null;

const upsertSavedRow = (currentRows = [], savedRow = null) => {
	if (!savedRow?.id) return currentRows;
	const savedId = String(savedRow.id);
	return [savedRow, ...currentRows.filter((row) => String(row?.id) !== savedId)];
};

const PromotionCodes = () => {
	const { t } = useTranslation();
	const { user, hasPermission } = useAuth();
	const compactTagLimit = useMediaQuery("(max-width:1366px)") ? 1 : 2;
	const [rows, setRows] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingFilters, setLoadingFilters] = useState(false);
	const [scopeLookupsReady, setScopeLookupsReady] = useState(false);
	const [error, setError] = useState("");
	const [modalOpen, setModalOpen] = useState(false);
	const [editRow, setEditRow] = useState(null);
	const [detailRow, setDetailRow] = useState(null);
	const [scrollToRowId, setScrollToRowId] = useState(null);
	const [form, setForm] = useState(EMPTY_FORM);
	const [broadDisplayState, setBroadDisplayState] = useState(EMPTY_BROAD_DISPLAY_STATE);
	const [fieldErrors, setFieldErrors] = useState({});
	const [saving, setSaving] = useState(false);
	const [sites, setSites] = useState([]);
	const [machines, setMachines] = useState([]);
	const [customersOptions, setCustomersOptions] = useState([]);
	const [sitesOptions, setSitesOptions] = useState([]);
	const [productOptions, setProductOptions] = useState([]);

	const [customerValue, setCustomerValue] = useState(null);
	const [locationValue, setLocationValue] = useState(null);
	const [machineValue, setMachineValue] = useState(null);
	const [customerInput, setCustomerInput] = useState("");
	const [locationInput, setLocationInput] = useState("");
	const [machineInput, setMachineInput] = useState("");

	const [promotionCustomerInput, setPromotionCustomerInput] = useState("");
	const [promotionLocationInput, setPromotionLocationInput] = useState("");
	const [promotionMachineInput, setPromotionMachineInput] = useState("");
	const [promotionProductInput, setPromotionProductInput] = useState("");
	const [loadingProducts, setLoadingProducts] = useState(false);
	const containerRef = useRef(null);
	const formRef = useRef(null);
	const todayDate = useMemo(() => getTodayDate(), []);
	const isSuperAdmin = Number(user?.user_id) === 1;
	const canCreate = isSuperAdmin || hasPermission("promotion_management.create_promotions");
	const canEdit = isSuperAdmin || hasPermission("promotion_management.edit_promotions");
	const canDelete = isSuperAdmin || hasPermission("promotion_management.delete_promotions");
	const actorRoleType = useMemo(
		() =>
			String(
				user?.role?.role_type ||
				user?.role?.role_name ||
				user?.user_role_name ||
				user?.user_role ||
				""
			)
				.trim()
				.toLowerCase(),
		[user?.role?.role_type, user?.role?.role_name, user?.user_role_name, user?.user_role]
	);
	const directActorSiteIds = useMemo(
		() =>
			Array.from(
				new Set(
					(Array.isArray(user?.allocations) ? user.allocations : [])
						.map((allocation) => Number(allocation?.site_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				)
			),
		[user?.allocations]
	);
	const [fallbackActorSiteIds, setFallbackActorSiteIds] = useState([]);
	useEffect(() => {
		let mounted = true;
		const loadFallbackActorScope = async () => {
			if (isSuperAdmin || directActorSiteIds.length > 0 || !user?.user_id) {
				if (mounted) setFallbackActorSiteIds([]);
				return;
			}
			try {
				const response = await getUserAllocations(user.user_id);
				const allocations = Array.isArray(response?.data?.allocations) ? response.data.allocations : [];
				const nextIds = Array.from(
					new Set(
						allocations
							.map((allocation) => Number(allocation?.site_id))
							.filter((id) => Number.isFinite(id) && id > 0)
					)
				);
				if (mounted) setFallbackActorSiteIds(nextIds);
			} catch (_error) {
				if (mounted) setFallbackActorSiteIds([]);
			}
		};
		loadFallbackActorScope();
		return () => {
			mounted = false;
		};
	}, [directActorSiteIds, isSuperAdmin, user?.user_id]);
	const effectiveActorSiteIds = useMemo(
		() => (directActorSiteIds.length > 0 ? directActorSiteIds : fallbackActorSiteIds),
		[directActorSiteIds, fallbackActorSiteIds]
	);
	const actorHasAllSiteVisibilityForFilters = useMemo(
		() =>
			isSuperAdmin ||
			((actorRoleType === "admin" || hasPermission("user_management.manage_all_sites")) &&
				effectiveActorSiteIds.length === 0),
		[actorRoleType, effectiveActorSiteIds.length, hasPermission, isSuperAdmin]
	);
	const scopedRows = useMemo(() => {
		if (actorHasAllSiteVisibilityForFilters) return rows;
		const allowedSiteIds = new Set(effectiveActorSiteIds.map((id) => Number(id)));
		return rows.filter((row) => {
			const scopedSiteIds = toIdArray(row?.customer_site_ids);
			return scopedSiteIds.length > 0 && scopedSiteIds.every((siteId) => allowedSiteIds.has(Number(siteId)));
		});
	}, [actorHasAllSiteVisibilityForFilters, effectiveActorSiteIds, rows]);

	const modalAutocompleteProps = useMemo(
		() => ({
			disablePortal: false,
			disableCloseOnSelect: true,
			PopperProps: {
				className: "modal-menu-popover",
				sx: { zIndex: 2000 },
				modifiers: [
					{ name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
					{ name: "flip", options: { padding: 8 } },
				],
			},
			slotProps: {
				popper: { className: "modal-menu-popover", sx: { zIndex: 2000 } },
				paper: { sx: { maxHeight: 320, overflowY: "auto" } },
				listbox: { sx: { maxHeight: 300, overflowY: "auto" } },
			},
			ListboxProps: { style: { maxHeight: 300, overflowY: "auto" } },
		}),
		[]
	);

	const fetchPromotions = useCallback(async ({ silent = false } = {}) => {
		if (!silent) setLoading(true);
		setError("");
		try {
			const response = await getPromotionCodes();
			if (response?.status === false) {
				setError(getErrorMessage(response, "Failed to load promotion codes"));
				setRows([]);
				return;
			}
			const data = Array.isArray(response?.data) ? response.data : Array.isArray(response) ? response : [];
			setRows(data);
		} catch (err) {
			setError(err?.message || "Failed to load promotion codes");
			setRows([]);
		} finally {
			if (!silent) setLoading(false);
		}
	}, []);

	const fetchCustomers = useCallback(async () => {
		try {
			const [customersResponse, sitesResponse] = await Promise.all([
				getCustomersCusSites(),
				getAllCustomerSites(),
			]);
			if (customersResponse.status) {
				const allCustomers = (customersResponse.data || []).filter(
					(customer) => customer.customer_name && customer.customer_name !== "EMPTY"
				);
				const visibleSites = actorHasAllSiteVisibilityForFilters
					? (sitesResponse?.data || [])
					: (sitesResponse?.data || []).filter((site) => effectiveActorSiteIds.includes(Number(site.site_id)));
				const eligibleCustomerIds = new Set(
					visibleSites
						.map((site) => Number(site.cus_id))
						.filter((id) => Number.isFinite(id) && id > 0)
				);
				const filteredCustomers = allCustomers.filter((customer) =>
					eligibleCustomerIds.has(Number(customer.cus_id))
				);
				setCustomersOptions(
					filteredCustomers.map((customer) => ({
						value: customer.cus_id,
						label: `${customer.customer_name} - ${customer.customer_number}`,
					}))
				);
			}
		} catch (err) {
			console.error("Error fetching customers:", err);
		}
	}, [actorHasAllSiteVisibilityForFilters, effectiveActorSiteIds]);

	const fetchSites = useCallback(async () => {
		try {
			const response = await getAllCustomerSites();
			if (response.status) {
				const allSites = (response.data || []).filter(
					(site) => site.customer_site_name && site.customer_site_name !== "EMPTY"
				);
				const filteredSites = actorHasAllSiteVisibilityForFilters
					? allSites
					: allSites.filter((site) => effectiveActorSiteIds.includes(Number(site.site_id)));
				setSites(filteredSites);
				setSitesOptions(
					filteredSites.map((site) => ({
						value: site.site_id,
						customer_id: site.cus_id,
						label: `${site.customer_site_name} - ${site.customer_site_number}`,
					}))
				);
			}
		} catch (err) {
			console.error("Error fetching sites:", err);
		}
	}, [actorHasAllSiteVisibilityForFilters, effectiveActorSiteIds]);

	const fetchMachines = useCallback(async () => {
		try {
			const response = await getMachinesByProvision();
			if (response.status) {
				const allMachines = response.data || [];
				const filteredMachines = actorHasAllSiteVisibilityForFilters
					? allMachines
					: allMachines.filter((machine) => effectiveActorSiteIds.includes(Number(machine.site_id)));
				setMachines(filteredMachines);
			}
		} catch (err) {
			console.error("Error fetching machines:", err);
		}
	}, [actorHasAllSiteVisibilityForFilters, effectiveActorSiteIds]);

	useEffect(() => {
		fetchPromotions();
	}, [fetchPromotions]);

	useEffect(() => {
		if (!modalOpen) return;
		let frameOne = 0;
		let frameTwo = 0;
		frameOne = window.requestAnimationFrame(() => {
			containerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			frameTwo = window.requestAnimationFrame(() => {
				formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
			});
		});
		return () => {
			window.cancelAnimationFrame(frameOne);
			window.cancelAnimationFrame(frameTwo);
		};
	}, [modalOpen, editRow?.id]);

	useEffect(() => {
		if (!user) return;
		const init = async () => {
			setLoadingFilters(true);
			setScopeLookupsReady(false);
			await Promise.all([fetchCustomers(), fetchSites(), fetchMachines()]);
			setScopeLookupsReady(true);
			setLoadingFilters(false);
		};
		init();
	}, [fetchCustomers, fetchMachines, fetchSites, user]);

	const debouncedCustomerInput = useMemo(
		() => debounce((value) => {
			setCustomerInput(value);
		}, 300),
		[]
	);

	const debouncedLocationInput = useMemo(
		() => debounce((value) => {
			setLocationInput(value);
		}, 300),
		[]
	);

	const debouncedMachineInput = useMemo(
		() => debounce((value) => {
			setMachineInput(value);
		}, 300),
		[]
	);

	const debouncedPromotionCustomerInput = useMemo(
		() => debounce((value) => {
			setPromotionCustomerInput(value);
		}, 300),
		[]
	);

	const debouncedPromotionLocationInput = useMemo(
		() => debounce((value) => {
			setPromotionLocationInput(value);
		}, 300),
		[]
	);

	const debouncedPromotionMachineInput = useMemo(
		() => debounce((value) => {
			setPromotionMachineInput(value);
		}, 300),
		[]
	);

	const debouncedPromotionProductInput = useMemo(
		() => debounce((value) => {
			setPromotionProductInput(value);
		}, 300),
		[]
	);

	const ALL_CUSTOMERS_OPTION = useMemo(() => ({
		value: t("ADMIN_FILTERS.ALL_CUSTOMERS"),
		label: t("ADMIN_FILTERS.ALL_CUSTOMERS"),
	}), [t]);

	const ALL_LOCATIONS_OPTION = useMemo(() => ({
		value: t("ADMIN_FILTERS.ALL_LOCATIONS"),
		label: t("ADMIN_FILTERS.ALL_LOCATIONS"),
	}), [t]);

	const ALL_MACHINES_OPTION = useMemo(() => ({
		value: t("ADMIN_FILTERS.ALL_MACHINES"),
		label: t("ADMIN_FILTERS.ALL_MACHINES"),
	}), [t]);

	const isAllCustomersSelected = useMemo(
		() => customerValue?.value === ALL_CUSTOMERS_OPTION.value,
		[customerValue, ALL_CUSTOMERS_OPTION.value]
	);
	const isAllLocationsSelected = useMemo(
		() => locationValue?.value === ALL_LOCATIONS_OPTION.value,
		[locationValue, ALL_LOCATIONS_OPTION.value]
	);
	const isAllMachinesSelected = useMemo(
		() => machineValue?.value === ALL_MACHINES_OPTION.value,
		[machineValue, ALL_MACHINES_OPTION.value]
	);

	const findCustomerOption = useCallback(
		(cusId) => customersOptions.find((customer) => String(customer?.value) === String(cusId)) || null,
		[customersOptions]
	);

	const findSiteOption = useCallback(
		(siteId) => sitesOptions.find((site) => String(site?.value) === String(siteId)) || null,
		[sitesOptions]
	);

	const findMachine = useCallback(
		(machineId) => machines.find((machine) => String(machine?.machine_id) === String(machineId)) || null,
		[machines]
	);

	const findSite = useCallback(
		(siteId) => sites.find((site) => String(site?.site_id) === String(siteId)) || null,
		[sites]
	);

	const getMachineSiteNumber = useCallback(
		(machine = {}) => {
			const site = findSite(machine.site_id);
			return (
				site?.customer_site_number ||
				site?.site_number ||
				machine?.customer_site_number ||
				machine?.site_number ||
				""
			);
		},
		[findSite]
	);

	const machinesOptions = useMemo(
		() =>
			machines.map((machine) => ({
				value: machine.machine_id,
				label: `${machine.machine_name} - ${machine.machine_number}`,
			})),
		[machines]
	);

	const filteredCustomersOptions = useMemo(() => {
		if (!customerInput.trim()) return customersOptions;
		return customersOptions.filter((option) =>
			option.label.toLowerCase().includes(customerInput.toLowerCase())
		);
	}, [customersOptions, customerInput]);

	const filteredSitesOptions = useMemo(() => {
		let filtered = sitesOptions;

		if (customerValue?.value && !isAllCustomersSelected) {
			filtered = filtered.filter((option) =>
				String(sites.find((site) => site.site_id === option.value)?.cus_id) === String(customerValue.value)
			);
		}

		if (locationInput.trim() && !isAllLocationsSelected) {
			filtered = filtered.filter((option) =>
				option.label.toLowerCase().includes(locationInput.toLowerCase())
			);
		}

		return filtered;
	}, [customerValue?.value, isAllCustomersSelected, isAllLocationsSelected, locationInput, sites, sitesOptions]);

	const filteredMachinesOptions = useMemo(() => {
		let filtered = machines;

		if (locationValue?.value && !isAllLocationsSelected) {
			filtered = filtered.filter((machine) => String(machine.site_id) === String(locationValue.value));
		} else if (customerValue?.value && !isAllCustomersSelected) {
			filtered = filtered.filter((machine) => String(machine.cus_id) === String(customerValue.value));
		}

		const options = filtered.map((machine) => ({
			value: machine.machine_id,
			label: `${machine.machine_name} - ${machine.machine_number}`,
		}));

		if (machineInput.trim()) {
			return options.filter((option) =>
				option.label.toLowerCase().includes(machineInput.toLowerCase())
			);
		}

		return options;
	}, [customerValue?.value, isAllCustomersSelected, isAllLocationsSelected, locationValue?.value, machineInput, machines]);

	const selectedCustomerOptions = useMemo(
		() => optionsByIds(customersOptions, form.customer_ids),
		[customersOptions, form.customer_ids]
	);

	const selectedSiteOptions = useMemo(
		() => selectedOrAllOptionsForDisplay(sitesOptions, form.customer_site_ids, broadDisplayState.customer_site_ids),
		[broadDisplayState.customer_site_ids, form.customer_site_ids, sitesOptions]
	);

	const selectedMachineOptions = useMemo(
		() => selectedOrAllOptionsForDisplay(machinesOptions, form.machine_ids, broadDisplayState.machine_ids),
		[broadDisplayState.machine_ids, form.machine_ids, machinesOptions]
	);

	const selectedProductOptions = useMemo(
		() => selectedOrAllOptionsForDisplay(productOptions, form.product_ids, broadDisplayState.product_ids),
		[broadDisplayState.product_ids, form.product_ids, productOptions]
	);

	const promotionScopedCustomersOptions = useMemo(() => {
		return customersOptions;
	}, [customersOptions]);

	const promotionFilteredCustomersOptions = useMemo(() => {
		if (!promotionCustomerInput.trim()) return promotionScopedCustomersOptions;
		return promotionScopedCustomersOptions.filter((option) =>
			option.label.toLowerCase().includes(promotionCustomerInput.toLowerCase())
		);
	}, [promotionCustomerInput, promotionScopedCustomersOptions]);

	const promotionScopedSitesOptions = useMemo(() => {
		let filtered = sitesOptions;
		const selectedCustomerIds = toIdArray(form.customer_ids);

		if (selectedCustomerIds.length > 0) {
			filtered = filtered.filter((option) =>
				selectedCustomerIds.includes(Number(option.customer_id))
			);
		}

		return filtered;
	}, [form.customer_ids, sitesOptions]);

	const promotionFilteredSitesOptions = useMemo(() => {
		let filtered = promotionScopedSitesOptions;

		if (promotionLocationInput.trim()) {
			filtered = filtered.filter((option) =>
				option.label.toLowerCase().includes(promotionLocationInput.toLowerCase())
			);
		}

		return filtered;
	}, [promotionLocationInput, promotionScopedSitesOptions]);

	const promotionFilteredMachinesOptions = useMemo(() => {
		let filtered = machines;
		const selectedCustomerIds = toIdArray(form.customer_ids);
		const selectedSiteIds = toIdArray(form.customer_site_ids);

		if (selectedSiteIds.length > 0) {
			filtered = filtered.filter((machine) => selectedSiteIds.includes(Number(machine.site_id)));
		} else if (selectedCustomerIds.length > 0) {
			filtered = filtered.filter((machine) => selectedCustomerIds.includes(Number(machine.cus_id)));
		}

		const options = filtered.map((machine) => ({
			value: machine.machine_id,
			label: `${machine.machine_name} - ${machine.machine_number}`,
		}));

		if (promotionMachineInput.trim()) {
			return options.filter((option) =>
				option.label.toLowerCase().includes(promotionMachineInput.toLowerCase())
			);
		}

		return options;
	}, [
		form.customer_ids,
		form.customer_site_ids,
		machines,
		promotionMachineInput,
	]);

	const promotionFilteredProductOptions = useMemo(() => {
		if (!promotionProductInput.trim()) return productOptions;
		return productOptions.filter((option) =>
			option.label.toLowerCase().includes(promotionProductInput.toLowerCase())
		);
	}, [productOptions, promotionProductInput]);

	useEffect(() => {
		if (!modalOpen) return;

		const machineIds = toIdArray(form.machine_ids);
		if (machineIds.length === 0) {
			setProductOptions([]);
			setForm((prev) =>
				toIdArray(prev.product_ids).length ? { ...prev, product_ids: [] } : prev
			);
			return;
		}

		let active = true;
		const selectedMachines = machines.filter((machine) =>
			machineIds.includes(Number(machine.machine_id))
		);

		const loadProducts = async () => {
			setLoadingProducts(true);
			try {
				const resultGroups = await Promise.all(
					selectedMachines.map(async (machine) => {
						const siteNumber = getMachineSiteNumber(machine);
						if (!machine?.machine_id || !siteNumber) return [];
						const response = await getProductsByMachineId({
							machine_id: machine.machine_id,
							site_number: siteNumber,
						});
						const { products } = normalizeMachineProductsResponse(response);
						return (products || []).map((product) => ({
							...product,
							machine_id: product.machine_id || machine.machine_id,
							machine_ids: [machine.machine_id],
							customer_site_ids: [machine.site_id],
							customer_ids: [machine.cus_id],
						}));
					})
				);

				if (!active) return;

				const byProductId = new Map();
				resultGroups.flat().forEach((product) => {
					const productId = Number(product?.product_id);
					if (!Number.isFinite(productId) || productId <= 0) return;
					const existing = byProductId.get(productId) || {
						value: productId,
						label: [
							product.product_name,
							product.product_number || product.pnum,
						].filter(Boolean).join(" - ") || `${productId}`,
						machine_ids: [],
						customer_site_ids: [],
						customer_ids: [],
					};
					existing.machine_ids = toIdArray([...existing.machine_ids, ...(product.machine_ids || [])]);
					existing.customer_site_ids = toIdArray([...existing.customer_site_ids, ...(product.customer_site_ids || [])]);
					existing.customer_ids = toIdArray([...existing.customer_ids, ...(product.customer_ids || [])]);
					byProductId.set(productId, existing);
				});

				const nextOptions = Array.from(byProductId.values()).sort((left, right) =>
					left.label.localeCompare(right.label, undefined, { numeric: true, sensitivity: "base" })
				);
				const availableProductIds = nextOptions.map((option) => Number(option.value));
				setProductOptions(nextOptions);
				setForm((prev) => {
					const nextProductIds = toIdArray(prev.product_ids).filter((productId) =>
						availableProductIds.includes(productId)
					);
					return nextProductIds.length === toIdArray(prev.product_ids).length
						? prev
						: { ...prev, product_ids: nextProductIds };
				});
			} catch (err) {
				console.error("Error fetching products for promotion:", err);
				if (active) setProductOptions([]);
			} finally {
				if (active) setLoadingProducts(false);
			}
		};

		loadProducts();
		return () => {
			active = false;
		};
	}, [form.machine_ids, getMachineSiteNumber, machines, modalOpen]);

	const displayRows = useMemo(
		() =>
			scopedRows.map((row) => ({
				...row,
				display_code: getPromotionCode(row),
				discount_display: formatDiscount(row),
				single_usage_display: row?.single_use_per_user ? "Once per user" : "Multiple use",
				scope_display: formatScope(row, {
					customersOptions,
					sitesOptions,
				}),
			})),
		[customersOptions, scopedRows, sitesOptions]
	);

	const filteredDisplayRows = useMemo(() => {
		const selectedCustomerId = !isAllCustomersSelected ? Number(customerValue?.value) : null;
		const selectedSiteId = !isAllLocationsSelected ? Number(locationValue?.value) : null;
		const selectedMachineId = !isAllMachinesSelected ? Number(machineValue?.value) : null;

		if (!selectedCustomerId && !selectedSiteId && !selectedMachineId) return displayRows;

		const selectedSite = selectedSiteId ? findSite(selectedSiteId) : null;
		const selectedMachine = selectedMachineId ? findMachine(selectedMachineId) : null;
		const customerId = selectedMachine?.cus_id || selectedSite?.cus_id || selectedCustomerId;
		const siteId = selectedMachine?.site_id || selectedSiteId;
		const machineId = selectedMachineId;
		const customerSiteIds = customerId
			? sites
				.filter((site) => String(site.cus_id) === String(customerId))
				.map((site) => site.site_id)
			: [];
		return displayRows.filter((row) => {
			const customerIds = toIdArray(row.customer_ids);
			const siteIds = toIdArray(row.customer_site_ids);
			const hasScope = customerIds.length > 0 || siteIds.length > 0;
			if (!hasScope) return true;

			if (machineId) {
				if (siteId && arrayIntersects(siteIds, [siteId])) return true;
				if (customerId && arrayIntersects(customerIds, [customerId])) return true;
				return false;
			}

			if (siteId) {
				if (arrayIntersects(siteIds, [siteId])) return true;
				if (customerId && arrayIntersects(customerIds, [customerId])) return true;
				return false;
			}

			if (customerId) {
				if (arrayIntersects(customerIds, [customerId])) return true;
				if (arrayIntersects(siteIds, customerSiteIds)) return true;
				return false;
			}

			return true;
		});
	}, [
		customerValue?.value,
		displayRows,
		findMachine,
		findSite,
		isAllCustomersSelected,
		isAllLocationsSelected,
		isAllMachinesSelected,
		locationValue?.value,
		machineValue?.value,
		sites,
	]);

	const getPromotionScopeDetails = useCallback((row) => {
		return {
			bakeries: isAllScopeSelection(customersOptions, row.customer_ids) ? [] : getScopedOptionLabels(customersOptions, row.customer_ids),
			locations: isAllScopeSelection(sitesOptions, row.customer_site_ids) ? [] : getScopedOptionLabels(sitesOptions, row.customer_site_ids),
		};
	}, [customersOptions, sitesOptions]);

	const openCreate = () => {
		setDetailRow(null);
		setEditRow(null);
		setForm(EMPTY_FORM);
		setBroadDisplayState(EMPTY_CREATE_BROAD_DISPLAY_STATE);
		setFieldErrors({});
		setPromotionCustomerInput("");
		setPromotionLocationInput("");
		setModalOpen(true);
	};

	const openEdit = (row) => {
		const nextForm = {
			code: row?.code || row?.coupon_code || "",
			discount_type: "percent",
			discount_value: row?.discount_value ?? "",
			single_use_per_user: Boolean(row?.single_use_per_user),
			start_date: toInputDate(row?.start_date),
			end_date: toInputDate(row?.end_date),
			is_active: Boolean(row?.is_active),
			customer_ids: toIdArray(row?.customer_ids),
			customer_site_ids: toIdArray(row?.customer_site_ids),
		};
		setDetailRow(null);
		setEditRow(row);
		setForm(nextForm);
		setBroadDisplayState({
			customer_ids: nextForm.customer_ids.length === 0,
			customer_site_ids: nextForm.customer_site_ids.length === 0,
		});
		setFieldErrors({});
		setPromotionCustomerInput("");
		setPromotionLocationInput("");
		setModalOpen(true);
	};

	const closeModal = () => {
		setModalOpen(false);
		setEditRow(null);
		setForm(EMPTY_FORM);
		setBroadDisplayState(EMPTY_CREATE_BROAD_DISPLAY_STATE);
		setFieldErrors({});
		setSaving(false);
		setPromotionCustomerInput("");
		setPromotionLocationInput("");
	};

	const openDetail = (row) => {
		if (!row) return;
		setDetailRow(row);
	};

	const closeDetail = () => {
		setDetailRow(null);
	};

	const setField = (field, value) => {
		setForm((prev) => ({ ...prev, [field]: value }));
		setFieldErrors((prev) => {
			if (!prev[field] && !(field === "start_date" && prev.end_date)) return prev;
			const next = { ...prev };
			delete next[field];
			if (field === "start_date") delete next.end_date;
			return next;
		});
	};

	const validateForm = () => {
		const errors = {};
		const code = String(form.code || "").trim();
		const rawDiscountValue = String(form.discount_value || "").trim();
		const discountValue = Number(rawDiscountValue);

		if (!code) {
			errors.code = "Code is required.";
		} else if (code.length > MAX_PROMOTION_CODE_LENGTH) {
			errors.code = `Max length is ${MAX_PROMOTION_CODE_LENGTH} char`;
		}

		if (!rawDiscountValue) {
			errors.discount_value = "Discount percentage is required.";
		} else if (!isValidPercentageInput(rawDiscountValue)) {
			errors.discount_value = "Discount percentage must be a valid number.";
		} else if (discountValue <= 0) {
			errors.discount_value = "Discount percentage must be greater than 0.";
		} else if (discountValue > 100) {
			errors.discount_value = "Discount percentage cannot be greater than 100.";
		}

		if (!form.start_date) {
			errors.start_date = "Start date is required.";
		} else if (!isValidDateInput(form.start_date)) {
			errors.start_date = "Start date must be a valid date.";
		} else if (form.start_date < todayDate) {
			errors.start_date = "Start date cannot be earlier than today.";
		}

		if (!form.end_date) {
			errors.end_date = "End date is required.";
		} else if (!isValidDateInput(form.end_date)) {
			errors.end_date = "End date must be a valid date.";
		} else if (form.end_date < todayDate) {
			errors.end_date = "End date cannot be earlier than today.";
		} else if (
			form.start_date &&
			isValidDateInput(form.start_date) &&
			form.end_date < form.start_date
		) {
			errors.end_date = "End date cannot be earlier than start date.";
		}

		return errors;
	};

	// ─── handlePromotionCustomersChange ──────────────────────────────────────────
	const handlePromotionCustomersChange = (newValue = []) => {
		const selectedValues = Array.isArray(newValue) ? newValue : [];

		const selectedSpecificOptions = selectedValues.filter(
			(option) => String(option?.value) !== String(ALL_CUSTOMERS_OPTION.value)
		);

		const hasAllSelected = selectedValues.some(
			(option) => String(option?.value) === String(ALL_CUSTOMERS_OPTION.value)
		);

		if (hasAllSelected && selectedSpecificOptions.length === 0) {
			setBroadDisplayState((prev) => ({
				...prev,
				customer_ids: true,
				customer_site_ids: true,
			}));

			setForm((prev) => ({
				...prev,
				customer_ids: [],
				customer_site_ids: [],
			}));

			return;
		}

		setBroadDisplayState((prev) => ({
			...prev,
			customer_ids: false,
			customer_site_ids: false,
		}));

		const customerIds = idsFromOptions(selectedSpecificOptions);

		const validSiteIds = customerIds.length
			? sites
				.filter((site) => customerIds.includes(Number(site.cus_id)))
				.map((site) => Number(site.site_id))
			: [];

		setForm((prev) => ({
			...prev,
			customer_ids: customerIds,
			customer_site_ids: customerIds.length
				? toIdArray(prev.customer_site_ids).filter((siteId) =>
					validSiteIds.includes(siteId)
				)
				: [],
		}));
	};

	// ─── handlePromotionLocationsChange ──────────────────────────────────────────
	const handlePromotionLocationsChange = (newValue = []) => {
		const selectedValues = Array.isArray(newValue) ? newValue : [];

		const selectedSpecificOptions = selectedValues.filter(
			(option) => String(option?.value) !== String(ALL_LOCATIONS_OPTION.value)
		);

		const hasAllSelected = selectedValues.some(
			(option) => String(option?.value) === String(ALL_LOCATIONS_OPTION.value)
		);

		if (hasAllSelected && selectedSpecificOptions.length === 0) {
			setBroadDisplayState((prev) => ({
				...prev,
				customer_site_ids: true,
			}));

			setForm((prev) => ({
				...prev,
				customer_site_ids: [],
			}));

			return;
		}

		setBroadDisplayState((prev) => ({
			...prev,
			customer_site_ids: false,
		}));

		const siteIds = idsFromOptions(selectedSpecificOptions);

		const selectedSites = sites.filter((site) =>
			siteIds.includes(Number(site.site_id))
		);

		const relatedCustomerIds = toIdArray(selectedSites.map((site) => site.cus_id));

		setForm((prev) => {
			const prevCustomerIds = toIdArray(prev.customer_ids);

			const nextCustomerIds =
				prevCustomerIds.length > 0
					? Array.from(new Set([...prevCustomerIds, ...relatedCustomerIds]))
					: relatedCustomerIds;

			return {
				...prev,
				customer_ids: siteIds.length ? nextCustomerIds : prev.customer_ids,
				customer_site_ids: siteIds,
			};
		});
	};

	// ─── hydratePromotionFieldFromAll ─────────────────────────────────────────────
	const hydratePromotionFieldFromAll = useCallback(
		(field) => {
			if (field === "customer_ids" && broadDisplayState.customer_ids) {
				setBroadDisplayState((prev) => ({
					...prev,
					customer_ids: false,
					// FIX: also reset location display to "All" so stale explicit
					// site IDs don't survive a bakery-level "hydrate to all" action
					customer_site_ids: true,
				}));
				setForm((prev) => ({
					...prev,
					customer_ids: promotionScopedCustomersOptions.map((o) => o.value),
					customer_site_ids: [], // reset so locations re-scope to new bakeries
				}));
				return;
			}
			if (field === "customer_site_ids" && broadDisplayState.customer_site_ids) {
				setBroadDisplayState((prev) => ({ ...prev, customer_site_ids: false }));
				setForm((prev) => ({
					...prev,
					customer_site_ids: promotionScopedSitesOptions.map((o) => o.value),
				}));
			}
		},
		[
			broadDisplayState,
			promotionScopedCustomersOptions,
			promotionScopedSitesOptions,
		]
	);

	const buildPayload = () => ({
		code: form.code.trim().toUpperCase(),
		discount_type: "percent",
		discount_value: Number(form.discount_value),
		minimum_cost: null,
		maximum_cost: null,
		minimum_quantity: null,
		maximum_quantity: null,
		single_use_per_user: Boolean(form.single_use_per_user),
		start_date: form.start_date || null,
		end_date: form.end_date || null,
		is_active: Boolean(form.is_active),
		customer_ids: toIdArray(form.customer_ids),
		customer_site_ids: resolveScopedSiteIds({
			sites,
			customerIds: form.customer_ids,
			explicitSiteIds: form.customer_site_ids,
		}),
	});

	const handleSave = async () => {
		const payload = buildPayload();
		const validationErrors = validateForm();
		if (Object.keys(validationErrors).length > 0) {
			setFieldErrors(validationErrors);
			return;
		}

		setSaving(true);
		setFieldErrors({});
		try {
			const response = editRow
				? await updatePromotionCode(editRow.id, payload)
				: await createPromotionCode(payload);

			if (response?.status === false) {
				Swal.fire({ icon: "error", title: "Promotion not saved", text: getErrorMessage(response, "Promotion could not be saved") });
				return;
			}

			const savedRow = getSavedPromotionRow(response);
			if (savedRow) {
				setRows((currentRows) => upsertSavedRow(currentRows, savedRow));
				setScrollToRowId(null);
				window.requestAnimationFrame(() => setScrollToRowId(savedRow.id));
			}
			closeModal();
			await fetchPromotions({ silent: true });
			Swal.fire({ icon: "success", title: "Saved", text: "Promotion code saved successfully." });
		} catch (err) {
			Swal.fire({ icon: "error", title: "Promotion not saved", text: err?.message || "Promotion could not be saved" });
		} finally {
			setSaving(false);
		}
	};

	const handleDeactivate = async (row) => {
		const result = await Swal.fire({
			icon: "warning",
			title: "Deactivate promotion code?",
			text: `${getPromotionCode(row)} will no longer apply at checkout.`,
			showCancelButton: true,
			confirmButtonText: "Deactivate",
		});
		if (!result.isConfirmed) return;

		try {
			const response = await deactivatePromotionCode(row.id);
			if (response?.status === false) {
				Swal.fire({ icon: "error", title: "Promotion not deactivated", text: getErrorMessage(response, "Promotion could not be deactivated") });
				return;
			}
			await fetchPromotions({ silent: true });
			if (detailRow?.id === row.id) closeDetail();
			if (editRow?.id === row.id) closeModal();
			Swal.fire({ icon: "success", title: "Deactivated", text: "Promotion code deactivated successfully." });
		} catch (err) {
			Swal.fire({ icon: "error", title: "Promotion not deactivated", text: err?.message || "Promotion could not be deactivated" });
		}
	};

	const columns = [
		{ field: "display_code", headerName: "Code", flex: 1 },
		{
			field: "discount_display",
			headerName: "Discount",
			flex: 1,
			renderCell: (params) => (
				<Chip size="small" color="primary" variant="outlined" label={params.row?.discount_display} />
			),
		},
		{
			field: "start_date",
			headerName: "Start Date",
			flex: 0.9,
			minWidth: 140,
			valueFormatter: (value) => formatDateDisplay(value, "Any"),
		},
		{
			field: "end_date",
			headerName: "End Date",
			flex: 0.9,
			minWidth: 140,
			valueFormatter: (value) => formatDateDisplay(value, "No end"),
		},
		{ field: "single_usage_display", headerName: "Usage", width: 140 },
		{ field: "scope_display", headerName: "Scope", flex: 1 },
		{
			field: "is_active",
			headerName: "Status",
			width: 120,
			renderCell: (params) => (
				<Chip
					size="small"
					color={params.row?.is_active ? "success" : "default"}
					label={params.row?.is_active ? "Active" : "Inactive"}
				/>
			),
		},
		{
			field: "updated_at",
			headerName: "Last Modified On",
			flex: 1,
			minWidth: 190,
			valueFormatter: (value) => formatDateTime(value) || "-",
		},
		{
			field: "updated_by_display_name",
			headerName: "Last Modified By",
			flex: 1.2,
			minWidth: 190,
			renderCell: (params) => (
				<Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "center", height: "100%", width: "100%" }}>
					<UserName size={24} name={params.row?.updated_by_display_name || "N/A"} />
				</Box>
			),
		},
		(canEdit || canDelete) && {
			field: "actions",
			headerName: "Actions",
			width: 130,
			sortable: false,
			filterable: false,
			renderCell: (params) => (
				<Box sx={{ display: "flex", gap: 0.5 }}>
					<Tooltip title="Edit">
						<IconButton size="small" onClick={(event) => { event.stopPropagation(); openEdit(params.row); }} disabled={!canEdit}>
							<EditIcon fontSize="small" />
						</IconButton>
					</Tooltip>
					<Tooltip title="Deactivate">
						<span>
							<IconButton
								size="small"
								color="error"
								disabled={!canDelete || !params.row?.is_active}
								onClick={(event) => { event.stopPropagation(); handleDeactivate(params.row); }}
							>
								<BlockIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Box>
			),
		},
	].filter(Boolean);

	const renderContent = () => {
		if (loading || loadingFilters || !scopeLookupsReady) return <DatagridLoading columns={10} rows={5} />;
		if (error) return <Alert severity="error">{error}</Alert>;
		const customButtons = [
			canCreate && {
				label: "Create Promo Code",
				icon: <AddIcon />,
				onClick: openCreate,
			},
			{
				label: "Refresh Promo code",
				icon: <RefreshIcon />,
				onClick: fetchPromotions,
				disabled: loading,
			},
		].filter(Boolean);
		return (
			<Datagrid
				data={filteredDisplayRows}
				columns={columns}
				rowIdField="id"
				customButtons={customButtons}
				gridSx={ADMIN_GRID_SX}
				showSearchBar
				checkboxSelection={false}
				autoHeight
				pageSize={5}
				scrollToRowId={scrollToRowId}
				rowClick
				onRowClick={(params) => openDetail(params.row)}
			/>
		);
	};

	const promotionMinDate = todayDate;

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Box ref={containerRef} sx={{ display: "flex", flexDirection: "column", rowGap: 2 }}>
				{/* 
			<Box
				sx={{
					mb: 1,
					mt: 1,
					display: "flex",
					flexDirection: "column",
					rowGap: "5px",
					background: "#eee",
					p: 2,
					borderRadius: "10px",
				}}
			>
				<Typography fontWeight={600} mb={0.5}>{t("ADMIN_FILTERS.TITLE")}</Typography>
				<Typography variant="body2" color="text.secondary" mb={2}>
					{t("ADMIN_FILTERS.DESCRIPTION")}
				</Typography>

				<Grid container spacing={2}>
					<Grid item xs={12} md={4}>
						<Autocomplete
							size="small"
							loading={loadingFilters}
							options={[ALL_CUSTOMERS_OPTION, ...filteredCustomersOptions]}
							value={customerValue}
							onChange={(event, newVal) => {
								if (!newVal || newVal.value === null || newVal.value === ALL_CUSTOMERS_OPTION.value) {
									setCustomerValue(null);
									setLocationValue(null);
									setMachineValue(null);
								} else {
									setCustomerValue(newVal);
									const selectedSite = locationValue?.value ? findSite(locationValue.value) : null;
									if (selectedSite && String(selectedSite.cus_id) !== String(newVal.value)) {
										setLocationValue(null);
									}
									const selectedMachine = machineValue?.value ? findMachine(machineValue.value) : null;
									if (selectedMachine && String(selectedMachine.cus_id) !== String(newVal.value)) {
										setMachineValue(null);
									}
								}
							}}
							onInputChange={(event, newInputValue) => {
								debouncedCustomerInput(newInputValue);
							}}
							isOptionEqualToValue={(option, value) => option?.value === value?.value}
							getOptionLabel={(option) => option?.label || ""}
							renderInput={(params) => (
								<TextField {...params} label={t("ADMIN_FILTERS.CUSTOMER")} placeholder={t("ADMIN_FILTERS.SEARCH_CUSTOMERS")} />
							)}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<Autocomplete
							size="small"
							loading={loadingFilters}
							options={[ALL_LOCATIONS_OPTION, ...filteredSitesOptions]}
							value={locationValue}
							onChange={(event, newVal) => {
								if (!newVal || newVal.value === null || newVal.value === ALL_LOCATIONS_OPTION.value) {
									setLocationValue(null);
									setMachineValue(null);
								} else {
									setLocationValue(newVal);
									const selectedSite = findSite(newVal.value);
									const relatedCustomer = selectedSite ? findCustomerOption(selectedSite.cus_id) : null;
									if (relatedCustomer) {
										setCustomerValue(relatedCustomer);
									}
									const selectedMachine = machineValue?.value ? findMachine(machineValue.value) : null;
									if (selectedMachine && String(selectedMachine.site_id) !== String(newVal.value)) {
										setMachineValue(null);
									}
								}
							}}
							onInputChange={(event, newInputValue) => {
								debouncedLocationInput(newInputValue);
							}}
							isOptionEqualToValue={(option, value) => option?.value === value?.value}
							getOptionLabel={(option) => option?.label || ""}
							renderInput={(params) => (
								<TextField {...params} label={t("ADMIN_FILTERS.LOCATION")} placeholder={t("ADMIN_FILTERS.SEARCH_LOCATIONS")} />
							)}
						/>
					</Grid>

					<Grid item xs={12} md={4}>
						<Autocomplete
							size="small"
							loading={loadingFilters}
							options={[ALL_MACHINES_OPTION, ...filteredMachinesOptions]}
							value={machineValue}
							onChange={(event, newVal) => {
								if (!newVal || newVal.value === null || newVal.value === ALL_MACHINES_OPTION.value) {
									setMachineValue(null);
								} else {
									setMachineValue(newVal);
									const selectedMachine = findMachine(newVal.value);
									if (selectedMachine) {
										const relatedLocation = findSiteOption(selectedMachine.site_id);
										const relatedCustomer = findCustomerOption(selectedMachine.cus_id);
										if (relatedLocation) {
											setLocationValue(relatedLocation);
										}
										if (relatedCustomer) {
											setCustomerValue(relatedCustomer);
										}
									}
								}
							}}
							onInputChange={(event, newInputValue) => {
								debouncedMachineInput(newInputValue);
							}}
							isOptionEqualToValue={(option, value) => option?.value === value?.value}
							getOptionLabel={(option) => option?.label || ""}
							renderInput={(params) => (
								<TextField {...params} label={t("ADMIN_FILTERS.MACHINE")} placeholder={t("ADMIN_FILTERS.SEARCH_MACHINES")} />
							)}
						/>
					</Grid>
				</Grid>
			</Box> */}

				{renderContent()}

				{detailRow && (
					(() => {
						const scopeDetails = getPromotionScopeDetails(detailRow);
						return (
							<ModalWrapper
								open={!!detailRow}
								setOpen={(open) => {
									if (!open) closeDetail();
								}}
								onClose={closeDetail}
								title={getPromotionCode(detailRow) || "Promotion Details"}
								subtitle="Review promotion scope, discount, and status."
								width="70vw"
								content={
									<Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
										<Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 2 }}>
											<Box>
												<Typography variant="caption" color="text.secondary">Discount</Typography>
												<Typography variant="body2" fontWeight={500}>{formatDiscount(detailRow)}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Status</Typography>
												<Box sx={{ mt: 0.75 }}>
													<Chip
														size="small"
														color={detailRow?.is_active ? "success" : "default"}
														label={detailRow?.is_active ? "Active" : "Inactive"}
													/>
												</Box>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Start Date</Typography>
												<Typography variant="body2" fontWeight={500}>{formatDateDisplay(detailRow?.start_date, "Any")}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">End Date</Typography>
												<Typography variant="body2" fontWeight={500}>{formatDateDisplay(detailRow?.end_date, "No end")}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Usage</Typography>
												<Typography variant="body2" fontWeight={500}>{detailRow?.single_use_per_user ? "Once per user" : "Multiple use"}</Typography>
											</Box>
											<Box sx={{ gridColumn: { xs: "auto", sm: "1 / -1" } }}>
												<Typography variant="caption" color="text.secondary">Scope</Typography>
												<Typography variant="body2" fontWeight={500}>
													{formatScope(detailRow, {
														customersOptions,
														sitesOptions,
													})}
												</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Bakery</Typography>
												<Typography variant="body2" fontWeight={500}>{scopeDetails.bakeries.join(", ") || "All Bakeries"}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Bakery Location</Typography>
												<Typography variant="body2" fontWeight={500}>{scopeDetails.locations.join(", ") || "All Bakery Locations"}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Last Modified On</Typography>
												<Typography variant="body2" fontWeight={500}>{formatDateTime(detailRow?.updated_at) || "-"}</Typography>
											</Box>
											<Box>
												<Typography variant="caption" color="text.secondary">Last Modified By</Typography>
												<Box sx={{ mt: 0.5 }}>
													<UserName size={24} name={detailRow?.updated_by_display_name || "N/A"} />
												</Box>
											</Box>
										</Box>
									</Box>
								}
								actions={
									<Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, width: "100%", gap: 2 }}>
										<Button variant="text" onClick={closeDetail}>
											Close
										</Button>
										<Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "flex-end", gap: 1 }}>
											<Button
												size="small"
												color="error"
												variant="contained"
												startIcon={<BlockIcon />}
												disabled={!canDelete || !detailRow?.is_active}
												onClick={() => handleDeactivate(detailRow)}
											>
												Deactivate
											</Button>
											<Button
												size="small"
												variant="contained"
												startIcon={<EditIcon />}
												disabled={!canEdit}
												onClick={() => {
													openEdit(detailRow);
													closeDetail();
												}}
											>
												Edit Promotion
											</Button>
										</Box>
									</Box>
								}
							/>
						);
					})()
				)}

				{modalOpen && (
					<Paper
						elevation={1}
						ref={formRef}
						sx={{
							width: "100%",
							mt: 2,
							p: 3,
							borderRadius: 2,
							display: "flex",
							flexDirection: "column",
							scrollMarginTop: { xs: "96px", md: "148px" },
						}}
					>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "flex-start",
								gap: 2,
								mb: 2,
								flexWrap: "wrap",
							}}
						>
							<Box>
								<Typography variant="h6">
									{editRow ? "Edit Promotion Code" : "Add Promotion Code"}
								</Typography>
								<Typography
									variant="body1"
									color="text.secondary"
								>
									Configure a percentage checkout code by bakery or bakery location.
								</Typography>
							</Box>

							<Button variant="outlined" size="small" onClick={closeModal}>
								Cancel
							</Button>
						</Box>

						<Box
							sx={{
								flexGrow: 1,
							}}
						>
							<Grid
								container
								spacing={2}
								sx={{
									"& .MuiFormControl-root": {
										width: "100%",
									},
									"& .MuiAutocomplete-tag": {
										maxWidth: "calc(100% - 32px)",
									},
								}}
							>
								<Grid item xs={12} md={6}>
									<TextField
										size="small"
										label="Code"
										value={form.code}
										onChange={(event) => setField("code", event.target.value.toUpperCase())}
										fullWidth
										required
										error={!!fieldErrors.code}
										helperText={fieldErrors.code || `${String(form.code || "").length}/${MAX_PROMOTION_CODE_LENGTH}`}
										inputProps={{ maxLength: MAX_PROMOTION_CODE_LENGTH }}
									/>
								</Grid>

								<Grid item xs={12} md={6}>
									<TextField
										size="small"
										label="Discount Percentage"
										type="text"
										value={form.discount_value}
										onChange={(event) => setField("discount_value", event.target.value)}
										inputProps={{ inputMode: "decimal" }}
										fullWidth
										required
										error={!!fieldErrors.discount_value}
										helperText={fieldErrors.discount_value}
									/>
								</Grid>

								<Grid item xs={12} md={6}>
									<DatePicker
										label="Start Date"
										format="MM-DD-YYYY"
										value={toDayjsDate(form.start_date)}
										onChange={(value) =>
											setField("start_date", value?.isValid() ? value.format("YYYY-MM-DD") : "")
										}
										minDate={dayjs(promotionMinDate)}
										disablePast
										slotProps={{
											textField: {
												size: "small",
												fullWidth: true,
												required: true,
												error: !!fieldErrors.start_date,
												helperText: fieldErrors.start_date || " ",
											},
										}}
									/>
								</Grid>

								<Grid item xs={12} md={6}>
									<DatePicker
										label="End Date"
										format="MM-DD-YYYY"
										value={toDayjsDate(form.end_date)}
										onChange={(value) =>
											setField("end_date", value?.isValid() ? value.format("YYYY-MM-DD") : "")
										}
										minDate={dayjs(form.start_date || promotionMinDate)}
										disablePast
										slotProps={{
											textField: {
												size: "small",
												fullWidth: true,
												required: true,
												error: !!fieldErrors.end_date,
												helperText: fieldErrors.end_date || " ",
											},
										}}
									/>
								</Grid>
								<Grid item xs={12} md={6}>
									<Autocomplete
										{...modalAutocompleteProps}
										multiple
										disableCloseOnSelect
										size="small"
										loading={loadingFilters}
										options={[ALL_CUSTOMERS_OPTION, ...promotionFilteredCustomersOptions]}
										inputValue={promotionCustomerInput}
										value={
											broadDisplayState.customer_ids
												? [ALL_CUSTOMERS_OPTION]
												: selectedOrAllOptionsForDisplay(
													promotionScopedCustomersOptions,
													form.customer_ids,
													false
												)
										}
										onChange={(event, newValue) => handlePromotionCustomersChange(newValue)}
										onInputChange={(event, newInputValue, reason) => {
											if (reason === "reset") return;
											setPromotionCustomerInput(newInputValue);
										}}
										isOptionEqualToValue={(option, value) =>
											String(option?.value) === String(value?.value)
										}
										getOptionLabel={(option) => option?.label || ""}
										renderTags={(value, getTagProps) =>
											renderCompactTags(
												value,
												getTagProps,
												"All Bakeries",
												promotionScopedCustomersOptions.length,
												compactTagLimit
											)
										}
										renderInput={(params) => (
											<TextField
												{...params}
												size="small"
												label={t("ADMIN_FILTERS.CUSTOMER")}
												placeholder={
													broadDisplayState.customer_ids || form.customer_ids.length > 0
														? ""
														: "Select Bakery"
												}
											/>
										)}
									/>
								</Grid>

								<Grid item xs={12} md={6}>
									<Autocomplete
										{...modalAutocompleteProps}
										multiple
										disableCloseOnSelect
										size="small"
										loading={loadingFilters}
										options={[ALL_LOCATIONS_OPTION, ...promotionFilteredSitesOptions]}
										inputValue={promotionLocationInput}
										value={
											broadDisplayState.customer_site_ids
												? [ALL_LOCATIONS_OPTION]
												: selectedOrAllOptionsForDisplay(
													promotionScopedSitesOptions,
													form.customer_site_ids,
													false
												)
										}
										onChange={(event, newValue) => handlePromotionLocationsChange(newValue)}
										onInputChange={(event, newInputValue, reason) => {
											if (reason === "reset") return;
											setPromotionLocationInput(newInputValue);
										}}
										isOptionEqualToValue={(option, value) =>
											String(option?.value) === String(value?.value)
										}
										getOptionLabel={(option) => option?.label || ""}
										renderTags={(value, getTagProps) =>
											renderCompactTags(
												value,
												getTagProps,
												"All Bakery Locations",
												promotionScopedSitesOptions.length,
												compactTagLimit
											)
										}
										renderInput={(params) => (
											<TextField
												{...params}
												size="small"
												label={t("ADMIN_FILTERS.LOCATION")}
												placeholder={
													broadDisplayState.customer_site_ids || form.customer_site_ids.length > 0
														? ""
														: "Select Bakery Location"
												}
											/>
										)}
									/>
								</Grid>

							</Grid>
						</Box>

						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "stretch",
								gap: 2,
								mt: 2,
							}}
						>
							<Box
								sx={{
									display: "flex",
									gap: 2,
									flexWrap: "wrap",
									alignItems: "center",
								}}
							>
								<FormControlLabel
									control={
										<Switch
											checked={form.is_active}
											onChange={(event) => setField("is_active", event.target.checked)}
										/>
									}
									label="Active status"
									sx={{ m: 0 }}
								/>

								<FormControlLabel
									control={
										<Switch
											checked={form.single_use_per_user}
											onChange={(event) =>
												setField("single_use_per_user", event.target.checked)
											}
										/>
									}
									label="Single usage per user"
									sx={{ m: 0 }}
								/>
							</Box>

							<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, alignItems: "center" }}>
								<Button
									variant="contained"
									onClick={handleSave}
									disabled={saving}
									startIcon={!saving ? <AddIcon /> : null}
								>
									{saving ? (
										<CircularProgress size={18} color="inherit" />
									) : editRow ? (
										"Update Promo Code"
									) : (
										"Create Promo Code"
									)}
								</Button>
							</Box>
						</Box>
					</Paper>
				)}
			</Box>
		</LocalizationProvider>
	);
};

export default PromotionCodes;
